import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import { notificationsAPI } from '../../services/api';
import Avatar from './Avatar';
import { Bell, Menu, X, Languages } from 'lucide-react';

const Navbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { language, toggleLanguage, isRTL, t } = useLanguage();
  const { displayName, displayLogo } = useWhiteLabel();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Refresh every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000);

      // Listen for notifications updates
      const handleNotificationsUpdate = (event) => {
        setUnreadCount(event.detail.count);
      };
      window.addEventListener('notificationsUpdated', handleNotificationsUpdate);

      return () => {
        clearInterval(interval);
        window.removeEventListener('notificationsUpdated', handleNotificationsUpdate);
      };
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 fixed w-full z-30 top-0 overflow-visible">
      <div className="px-4 sm:px-6 lg:px-8 w-full">
        <div className="flex justify-between items-center h-16 w-full">
          {/* Left side */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              <Menu className="h-6 w-6" />
            </button>
            <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0">
              {displayLogo ? (
                <img 
                  src={displayLogo} 
                  alt={displayName}
                  className="w-8 h-8 rounded-lg object-cover hidden sm:block"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg flex items-center justify-center text-white font-bold text-sm hidden sm:flex">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-lg font-semibold text-slate-800 tracking-tight">{displayName}</span>
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-md hover:bg-slate-200 transition-colors flex-shrink-0"
              title={language === 'en' ? 'Switch to Arabic' : 'Switch to English'}
            >
              <Languages className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </button>

            <div className="h-8 w-px bg-slate-200 mx-1 hidden sm:block flex-shrink-0"></div>

            {/* Notifications */}
            <Link
              to="/notifications"
              className="p-2 rounded-full text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition-colors relative flex-shrink-0"
            >
              <Bell className="w-5 h-5" />
              {/* Unread badge */}
              {unreadCount > 0 && (
                <span className={`absolute w-2 h-2 ${
                  isRTL ? 'left-1.5' : 'right-1.5'
                } top-1.5 bg-rose-500 rounded-full border-2 border-white`}></span>
              )}
            </Link>

            {/* User menu */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 sm:gap-3 focus:outline-none hover:opacity-80 transition-opacity"
              >
                <Avatar name={user?.name} imageUrl={user?.avatar_url} size="md" />
                <div className={`hidden md:block ${isRTL ? 'text-right' : 'text-left'}`}>
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                </div>
              </button>

              {/* Dropdown menu */}
              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserMenu(false)}
                  />
                  <div className={`absolute mt-2 w-48 rounded-xl shadow-soft-lg bg-white ring-1 ring-slate-200 z-20 overflow-hidden ${
                    isRTL ? 'left-0' : 'right-0'
                  }`}>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className={`block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                        onClick={() => setShowUserMenu(false)}
                      >
                        {t('profile')}
                      </Link>
                      <Link
                        to="/settings"
                        className={`block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                        onClick={() => setShowUserMenu(false)}
                      >
                        {t('settings')}
                      </Link>
                      <div className="border-t border-slate-100 my-1"></div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          logout();
                        }}
                        className={`block w-full px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors ${
                          isRTL ? 'text-right' : 'text-left'
                        }`}
                      >
                        {t('logout')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;


